<template>
  <div class="d-flex flex-column align-items-center w-100">
    <!-- Same layout as the card / PayNow screens (.ksk-pay, kiosk-theme2.css). -->
    <div v-if="status === 'idle'" class="ksk-pay">
      <div class="ksk-pay__icon ksk-pay__icon--pending">
        <span class="ksk-pay__spinner"></span>
      </div>
      <div class="ksk-pay__title">{{ $t("kiosk.payment.generatingQr") }}</div>
      <div class="ksk-pay__caption">{{ $t("kiosk.payment.waitMoment") }}</div>
    </div>

    <div v-else-if="status === 'qr'" class="ksk-pay">
      <div class="ksk-pay__title">{{ $t("kiosk.payment.scanToPay") }}</div>
      <div class="ksk-pay__caption">{{ $t("kiosk.payment.useShopbackApp") }}</div>
      <div class="ksk-pay__stage">
        <div class="ksk-pay__qr">
          <VueQRCodeComponent
            v-if="qrPayload"
            :text="qrPayload"
            size="480"
            color="#222222"
            bg-color="#ffffff"
            error-level="M"
            class="d-flex justify-content-center"
          />
        </div>
      </div>
      <div class="ksk-pay__extras">
        <img class="ksk-pay__logo" src="@/assets/shopback_pay.png" alt="ShopBack Pay" />
      </div>
    </div>

    <PaymentErrorScreen
      v-else-if="status === 'expired'"
      icon="timer_off"
      :title="$t('kiosk.payment.timeOut')"
      :message="$t('kiosk.payment.windowExpired')"
    />

    <PaymentErrorScreen
      v-else-if="status === 'failed'"
      :title="$t('kiosk.payment.failed')"
      :message="errorMessage"
    />
  </div>
</template>

<script>
import VueQRCodeComponent from "vue-qrcode-component";
import ShopBackService from "../../../services/shopback.service";
import PaymentErrorScreen from "./PaymentErrorScreen.vue";

const POLL_INTERVAL_MS = 3000;
const COUNTDOWN_SECONDS = 70;

export default {
  name: "ShopBackPayment",
  components: {
    VueQRCodeComponent,
    PaymentErrorScreen,
  },
  data() {
    return {
      // idle | qr | failed | expired. Starts at "idle": the component is
      // mounted while PaymentSelection is still saving the order (start() runs
      // after that), and "qr" here showed an empty QR frame before the
      // "Generating QR code…" state.
      status: "idle",
      referenceId: null,
      qrPayload: null,
      seconds: COUNTDOWN_SECONDS,
      canCancel: true,
      isCancelling: false,
      errorMessage: "",
      pollTimer: null,
      countdownTimer: null,
      payload: null, // { transactionId, receiptNo, branchCode, memberId, amount }
    };
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopCountdown();
  },
  computed: {
    /**
     * PaymentSelection.vue's shared footer reads this (via $refs) to decide
     * whether to show its Cancel Payment button — only during the first 30s
     * and only while ShopBack still allows it (not yet scanned/swiped).
     */
    canShowCancelButton() {
      return this.canCancel && COUNTDOWN_SECONDS - this.seconds < 30;
    },
  },
  methods: {
    /**
     * Called by the parent (PaymentSelection.vue) once the kiosk transaction
     * exists — either right after saveOrder() succeeds, or directly on a
     * retry within the same order. Emits `paid` on CAPTURED and `closed`
     * when the shopper backs out / the order expires / gets cancelled.
     */
    start(payload) {
      this.payload = payload;
      this.errorMessage = "";
      this.createOrder();
    },

    /**
     * Sets status AND emits it — PaymentSelection.vue's shared footer
     * (Retry / Other Methods / Back buttons) reacts to this to know when to
     * show itself and which buttons to render, since it can't otherwise see
     * inside this component's own state.
     */
    setStatus(value) {
      this.status = value;
      this.$emit("status-change", value);
    },

    async createOrder() {
      this.setStatus("idle");
      this.qrPayload = null;
      this.referenceId = null;
      this.seconds = COUNTDOWN_SECONDS;
      this.canCancel = true;

      try {
        const response = await ShopBackService.createOrder({
          transaction_id: this.payload.transactionId,
          amount: this.payload.amount,
          receipt_no: this.payload.receiptNo,
          branch_code: this.payload.branchCode,
          member_id: this.payload.memberId,
        });

        if (!response.data || !response.data.referenceId) {
          this.showFailed(this.$t("kiosk.payment.qrFailed"));
          return;
        }

        this.referenceId = response.data.referenceId;
        this.qrPayload = response.data.qrPayload;
        this.setStatus("qr");
        this.startPolling();
        this.startCountdown();
      } catch (error) {
        this.showFailed(error.response?.data?.result?.message);
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(this.checkStatus, POLL_INTERVAL_MS);
    },

    stopPolling() {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    startCountdown() {
      this.stopCountdown();
      this.countdownTimer = setInterval(() => {
        if (this.seconds > 0) {
          this.seconds--;
          return;
        }

        this.stopCountdown();

        // Not scanned yet when the clock ran out — cancel and release the
        // screen. If the shopper already scanned/swiped (canCancel is
        // false), do NOT cancel — ShopBack rejects it anyway once payment
        // is in flight — just keep polling until it resolves.
        if (this.canCancel) {
          this.expireOrder();
        }
      }, 1000);
    },

    stopCountdown() {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    },

    async checkStatus() {
      if (!this.referenceId) return;

      try {
        const response = await ShopBackService.getStatus({
          reference_id: this.referenceId,
        });
        const status = response.data?.status;

        if (status === "PENDING" || status === "PROCESSING") {
          this.canCancel = false;
        } else if (status === "CAPTURED") {
          this.stopPolling();
          this.stopCountdown();
          this.$emit("paid", {
            referenceId: this.referenceId,
            shopbackOrderId: response.data.shopbackOrderId,
          });
        } else if (status === "FAILED") {
          this.stopPolling();
          this.stopCountdown();
          this.showFailed(response.data.failureReason || this.$t("kiosk.payment.failed"));
        } else if (status === "CANCELLED" || status === "ABANDONED") {
          this.stopPolling();
          this.stopCountdown();
          this.$emit("closed");
        }
        // CREATED or unrecognised status: keep polling.
      } catch (error) {
        // Single network error — ignore and keep polling.
      }
    },

    async expireOrder() {
      this.stopPolling();

      if (this.referenceId) {
        try {
          await ShopBackService.cancel({ reference_id: this.referenceId });
        } catch (error) {
          // Best-effort — ShopBack expires unpaid orders server-side too.
        }
      }

      this.setStatus("expired");
    },

    showFailed(message) {
      this.stopPolling();
      this.stopCountdown();
      this.setStatus("failed");
      this.errorMessage = message || this.$t("kiosk.payment.failed");
    },

    retry() {
      this.createOrder();
    },

    /**
     * Returns true only if ShopBack actually cancelled the order — the
     * caller (PaymentSelection.vue's confirm-modal flow) uses this to decide
     * whether to show a "cancelled successfully" toast.
     */
    async cancelManually() {
      if (!this.canCancel || this.isCancelling || !this.referenceId) return false;

      this.isCancelling = true;
      try {
        const response = await ShopBackService.cancel({
          reference_id: this.referenceId,
        });

        if (response.data?.cancelled) {
          this.stopPolling();
          this.stopCountdown();
          this.$emit("closed");
          return true;
        }
        // cancelled === false: payment was already in flight server-side —
        // keep polling for the real result instead of releasing the screen.
        return false;
      } catch (error) {
        // Ignore — the countdown/poll will still resolve the order.
        return false;
      } finally {
        this.isCancelling = false;
      }
    },

    close() {
      this.stopPolling();
      this.stopCountdown();
      this.$emit("closed");
    },
  },
};
</script>
